<template>
  <div class="anchors-container">
    <div class="menu-container" ref="scroll">
      <div v-for="(menu, index) in menus" :key="index" @click="handleMenuChange(menu.value, index)">
        <slot :menu="menu">
          <div class="anchor" :class="{ 'is-active': index === currentIndex }">
            {{ menu.label }}
            <div class="anchor-suffix" :class="{ 'is-active': index === currentIndex }">·</div>
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import { getFirstScrollElement } from '@/common/utils/util.scroll.js'
import { easeInOutSine, Tween } from '@/common/utils/tween.js'

export default {
  props: {
    top: {
      type: Number,
      default: 0
    },
    menus: {
      type: Array,
      default: () => []
    },
    parent: {
      type: String,
      default: ''
    },
    // 默认水平，传入 false 则变为垂直滚动。
    vertical: {
      type: Boolean,
      default: false
    },
    // 导航栏本体是否 fixed
    fixed: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      menu: '',
      isScroll: true,
      isMounted: false,
      scrollTop: 0,
      anchorChange: false,
      rootScrollElement: '',
      currentIndex: 0
    }
  },

  mounted() {
    this.isMounted = true
    this.getScrollElement()
  },

  watch: {
    scrollTop(v) {
      if (this.anchorChange) {
        // 切换按钮会滚动视图，$nextTick 之后按钮值改变了，但滚动可能还没有结束，所以需要打个标记。
        this.isScroll = true
      }
    },
    currentIndex(val) {
      let left = this.$refs.scroll.children[val].getBoundingClientRect().left
      let right =
        document.documentElement.clientWidth -
        this.$refs.scroll.children[val].getBoundingClientRect().right
      if (left < 0) {
        this.scrollToView(left)
      } else if (right < 0) {
        this.scrollToView(-right)
      }
    }
  },
  methods: {
    scrollToView(distance) {
      let from = this.$refs.scroll.scrollLeft
      new Tween()
        .duration(500)
        .easing(easeInOutSine)
        .on('update', x => {
          this.$refs.scroll.scrollLeft = from + x * distance
        })
        .on('complete', x => {
          this.$refs.scroll.scrollLeft = from + distance
        })
        .animate()
    },
    handleMenuChange(select, index) {
      this.isScroll = false
      this.anchorChange = false
      // 滚动高度等于元素距离可视区头部高度减去元素自身高度与元素上边框高度以及滚动区距离可视区头部的高度。
      const scrollElement = document.querySelector(select)
      if (scrollElement && this.rootScrollElement) {
        const offsetTop = scrollElement.offsetTop + scrollElement.clientTop
        const offsetHeight = this.fixed ? 0 : this.$el.offsetHeight
        const top = offsetTop - offsetHeight

        // 缓动处理
        let from = this.rootScrollElement.scrollTop

        new Tween()
          .duration(500)
          .easing(easeInOutSine)
          .on('update', x => {
            this.rootScrollElement.scrollTop = from + x * (top - from)
          })
          .on('complete', x => {
            this.rootScrollElement.scrollTop = from + (top - from)
          })
          .animate()

        this.$nextTick(() => {
          this.anchorChange = true
          // 避免视野内的滚动导致无法实际滚动到位置使得按钮不点亮
          // setTimeout(() => { this.currentIndex = index }, 500)
        })
      }
    },

    getScrollElement() {
      if (!this.isMounted) return
      // 如果没有传入 parent 默认取第一个父级滚动元素
      const parent = this.parent
      let element = null
      if (parent) {
        element = document.querySelector(parent)
        // mount 之后 rootScrollElement 可能已经存在了，如果和上次一样就不做任何操作。
        if (element === this.rootScrollElement) return
      } else if (this.$el) {
        element = getFirstScrollElement(this.$el.parentElement)
      }
      if (element) {
        this.removeScrollEvent()
        this.rootScrollElement = element
        this.rootScrollElement.addEventListener('scroll', this.handleScroll)
      }
    },

    removeScrollEvent() {
      if (this.rootScrollElement) {
        this.rootScrollElement.removeEventListener('scroll', this.handleScroll)
      }
    },

    handleScroll() {
      const scrollTop = this.rootScrollElement.scrollTop
      this.scrollTop = scrollTop
      if (!this.isScroll) return
      const { menus } = this
      const offsetHeight = this.fixed ? 0 : this.$el.offsetHeight
      const scrollList = []
      menus.forEach(item => {
        const element = document.querySelector(item.value)
        if (element) {
          const top = element.offsetTop
          const rect = {
            top: top + element.clientTop - offsetHeight,
            bottom: top + element.offsetHeight - offsetHeight
          }
          scrollList.push(rect)
        }
      })
      // 遍历按钮元素的 top 和 bottom，查看当前滚动在那个元素的区间内。
      scrollList.some((it, index) => {
        if (scrollTop >= it.top && scrollTop < it.bottom) {
          const menu = menus[index].value
          if (menu) {
            this.menu = menu
            this.currentIndex = index
            return true
          }
        } else {
          // 当小于最小高度时，就等于最小高度
          if (scrollTop >= 0 && scrollTop < it.bottom) {
            const menu = menus[index].value
            if (menu) {
              this.menu = menu
              this.currentIndex = index
              return true
            }
          }
        }
      })
    }
  }
}
</script>

<style lang="stylus" scoped>
.anchors-container
  background #FFFFFF
  opacity 0.75

.menu-container
  display flex
  flex-direction column
  cursor pointer

.anchor
  font-size 14px
  color #999999
  display flex
  align-items center
  padding 10px
  cursor: pointer
  &-suffix
    margin-left 15px
    font-size 42px
    height 4px
    width @height
    overflow hidden
    background @color
    &.is-active
      background #cd974c

.is-active
  color #cd974c

.direction-vertical
  flex-direction column
</style>
